import { Middleware } from '@reduxjs/toolkit';
import { switchUser } from 'core/actions/product/state';
import {
  authGoogleRequest,
  authRequest,
  newSigninRequest,
  passwordRequest,
  systemSigninRequest,
  validationRequest,
} from 'core/services/authentication';
import { dialogueRequest, searchRequest, tokenRequest } from 'core/services/product';
import { RootState } from 'types';

interface IIdentity {
  organisationUserId: number;
  /** The same response also carries a player response, so the queue is being rebuilt anyway. */
  carriesQueue: boolean;
}

/**
 * The organisation user a response resolved to, or null when the response says
 * nothing about identity.
 *
 * Every server route that authenticates echoes `organisationUserId` — including
 * the learner token route, which mints one for anonymous learners. That makes it
 * the only reliable way to tell "this is still the same person" from "someone
 * else is using this browser now"; a JWT swap alone doesn't, because the player
 * keeps its queue in local storage across sign-ins.
 */
export function identityOf(action: { type: string; payload?: any }): IIdentity | null {
  let organisationUserId: number | undefined;
  let carriesQueue = false;

  switch (action.type) {
    // These carry a playerResponse: the new user's queue arrives with them.
    case tokenRequest.fulfilled.type:
    case systemSigninRequest.fulfilled.type:
      organisationUserId = action.payload?.organisationUserId;
      carriesQueue = true;
      break;
    case dialogueRequest.fulfilled.type:
      organisationUserId = action.payload?.auth?.organisationUserId;
      carriesQueue = true;
      break;
    // Plain auth responses — no queue, so the player has to ask for one.
    case authRequest.fulfilled.type:
    case authGoogleRequest.fulfilled.type:
    case newSigninRequest.fulfilled.type:
    case passwordRequest.fulfilled.type:
    case validationRequest.fulfilled.type:
    case searchRequest.fulfilled.type:
      organisationUserId = action.payload?.organisationUserId;
      break;
    default:
      return null;
  }

  // `int` on the wire: 0 means the route didn't fill it in, not user zero.
  if (!organisationUserId) return null;

  return { organisationUserId, carriesQueue };
}

/**
 * Keeps per-user state tied to the user it belongs to.
 *
 * Runs ahead of the reducers so the wipe is atomic with the response that
 * brought the new identity: the previous user's queue, register and player
 * position are gone before that response is reduced, and the response's own
 * data survives.
 */
export const userScopeMiddleware: Middleware = store => next => (action: any) => {
  const identity = identityOf(action);

  if (identity) {
    const current = (store.getState() as RootState).session.organisationUserId;

    if (current !== identity.organisationUserId)
      store.dispatch(
        switchUser({
          organisationUserId: identity.organisationUserId,
          source: action.type,
          carriesQueue: identity.carriesQueue,
        }),
      );
  }

  return next(action);
};
